import { Component, inject } from '@angular/core';
import { AppInformationService, AuthService, BrewersUnitOfWork } from 'app/core/core.module';
import { ProductUnitOfMeasure } from 'app/core/model/product-unit-of-measure';
import { InputDialogComponent } from 'app/shared/shared.module';
import { confirm } from 'devextreme/ui/dialog';
import _, { indexOf } from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ProductUomDialogComponent } from '../product-uom-dialog/product-uom-dialog.component';
import { ProductLocation } from 'app/core/model/product-location';
import { ProductLocationDialogComponent } from '../product-location-dialog/product-location-dialog.component';
import { CustomerTaxCode } from 'app/core/model/customer-tax-code';
import { CustomerTaxCodeDialogComponent } from '../customer-tax-code-dialog/customer-tax-code-dialog.component';
import { ProductCategoryDialogComponent } from '../product-category-dialog/product-category-dialog.component';
import { ProductCategory } from 'app/core/model/product-category';
import { Setting } from 'app/core/model/entity-model';
@Component({
    standalone: false,
    selector: 'app-lookup-table',
    templateUrl: './lookup-table.component.html',
    styleUrl: './lookup-table.component.scss',
})
export class LookupTableComponent {
    uow = inject(BrewersUnitOfWork);
    modalService = inject(BsModalService);
    appInfo = inject(AppInformationService);
    auth = inject(AuthService);

    tables = ['Customer Tax Codes', 'Invoice Services', 'Mix Strengths', 'Product Categories', 'Product Locations', 'Product Materials', 'Product Units Of Measure'];
    table: string = this.tables[0];
    entities: any[];

    invoiceSubject: Setting;
    invoiceBody: Setting;
    quoteSubject: Setting;
    quoteBody: Setting;
    statementSubject: Setting;
    statementBody: Setting;

    get maxDisplayOrder() {
        return (_.maxBy(this.entities, 'displayOrder')?.displayOrder ?? 0) + 1;
    }

    async ngOnInit() {
        const settings = await this.uow.getSettings();
        this.invoiceSubject = settings.find(s => s.name === 'InvoiceSubject');
        this.invoiceBody = settings.find(s => s.name === 'InvoiceBody');
        this.quoteSubject = settings.find(s => s.name === 'QuoteSubject');
        this.quoteBody = settings.find(s => s.name === 'QuoteBody');
        this.statementSubject = settings.find(s => s.name === 'StatementSubject');
        this.statementBody = settings.find(s => s.name === 'StatementBody');

        await this.getData();
    }

    async getData() {
        switch (this.table) {
            case 'Customer Tax Codes':
                this.entities = await this.uow.customerTaxCodes.whereActive('displayOrder, id');
                break;
            case 'Invoice Services':
                this.entities = await this.uow.invoiceServices.whereActive('displayOrder, id');
                break;
            case 'Mix Strengths':
                this.entities = await this.uow.mixStrengths.whereActive('displayOrder, id');
                break;
            case 'Product Categories':
                this.entities = await this.uow.productCategories.whereActive('displayOrder, id');
                break;
            case 'Product Locations':
                this.entities = await this.uow.productLocations.whereActive('displayOrder, id');
                break;
            case 'Product Materials':
                this.entities = await this.uow.productMaterials.whereActive('displayOrder, id');
                break;
            case 'Product Units Of Measure':
                this.entities = await this.uow.productUnitsOfMeasure.whereActive('displayOrder, id');
                break;
            default:
                this.entities = [];
                break;
        }
    }

    canDeactivate() {
        return !this.uow.hasChanges();
    }

    async selectionChanged() {
        await this.getData();
    }

    onReorder = async (e: any) => {
        var visibleRows = e.component.getVisibleRows(),
            toIndex = this.entities.indexOf(visibleRows[e.toIndex].data),
            fromIndex = this.entities.indexOf(e.itemData);

        this.entities.splice(fromIndex, 1);
        this.entities.splice(toIndex, 0, e.itemData);

        this.updateDisplayOrder(this.entities);
        this.sort(this.entities);
        await this.uow.commit();
    };

    updateDisplayOrder(entities: any[]) {
        entities.forEach((entity, index) => {
            if (entity.displayOrder !== index + 1) entity.displayOrder = index + 1;
        });
    }

    sort(entities: any[]) {
        entities.sort((a, b) => (a.displayOrder || 9999) - (b.displayOrder || 9999));
    }

    async rowDoubleClick(e) {
        await this.openSelection(e.data);
    }

    async openSelection(entity?: any) {
        switch (this.table) {
            case 'Customer Tax Codes':
                await this.openCustomerTaxCode(entity);
                break;
            case 'Product Units Of Measure':
                this.openProductUom(entity);
                break;
            case 'Product Categories':
                this.openProductCategory(entity);
                break;
            case 'Product Locations':
                this.openProductLocation(entity);
                break;
            default:
                this.open(entity);
                break;
        }
    }

    async openCustomerTaxCode(customerTaxCode?: CustomerTaxCode) {
        const modalRef = this.modalService.show(CustomerTaxCodeDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                customerTaxCode: customerTaxCode,
            },
        });
        modalRef.content?.result.subscribe(async (result) => {
            if (result.success && result.value) {
                this.appInfo.pleaseWaitShow();
                customerTaxCode = result.value;
                if (!customerTaxCode.displayOrder) customerTaxCode.displayOrder = this.maxDisplayOrder;
                await this.uow.commit();
                await this.getData();
            } else this.uow.rollback();
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openProductUom(uom?: ProductUnitOfMeasure) {
        const modalRef = this.modalService.show(ProductUomDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                uom: uom,
            },
        });
        modalRef.content?.result.subscribe(async (result) => {
            if (result.success && result.value) {
                this.appInfo.pleaseWaitShow();
                uom = result.value;
                if (!uom.displayOrder) uom.displayOrder = this.maxDisplayOrder;
                await this.uow.commit();
                await this.getData();
            } else this.uow.rollback();
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openProductCategory(productCategory: ProductCategory) {
        const modalRef = this.modalService.show(ProductCategoryDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                productCategory: productCategory,
            },
        });
        modalRef.content?.result.subscribe(async (result) => {
            if (result.success && result.value) {
                this.appInfo.pleaseWaitShow();
                productCategory = result.value;
                if (!productCategory.displayOrder) productCategory.displayOrder = this.maxDisplayOrder;
                await this.uow.commit();
                await this.getData();
            } else this.uow.rollback();
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openProductLocation(location: ProductLocation) {
        const modalRef = this.modalService.show(ProductLocationDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                location: location,
            },
        });
        modalRef.content?.result.subscribe(async (result) => {
            if (result.success && result.value) {
                this.appInfo.pleaseWaitShow();
                location = result.value;
                if (!location.displayOrder) location.displayOrder = this.maxDisplayOrder;
                await this.uow.commit();
                await this.getData();
            } else this.uow.rollback();
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async open(entity?: any) {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: entity ? 'Edit Item' : 'Enter New Item',
                dialogValue: entity?.name,
                mode: 'text',
            },
        });
        modalRef.content?.result.subscribe(async (result) => {
            if (result.success && result.value) {
                this.appInfo.pleaseWaitShow();
                switch (this.table) {
                    case 'Invoice Services':
                        entity = entity ?? this.uow.invoiceServices.createEntity();
                        break;
                    case 'Mix Strengths':
                        entity = entity ?? this.uow.mixStrengths.createEntity();
                        break;
                    case 'Product Materials':
                        entity = entity ?? this.uow.productMaterials.createEntity();
                        break;
                }
                if (entity) {
                    entity.name = result.value;
                    if (!entity.displayOrder) entity.displayOrder = this.maxDisplayOrder;
                    await this.uow.commit();
                    await this.getData();
                }
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async delete(entity: any) {
        const result = await confirm('Are you sure you want to delete this item?', this.appInfo.settings.company);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        entity.deleted = new Date();
        entity.deletedBy = this.auth.currentUser.userName;
        this.entities.splice(indexOf(this.entities, entity), 1);
        this.updateDisplayOrder(this.entities);
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async save() {
        await this.appInfo.pleaseWaitShow();
        await this.uow.commit();
        await this.appInfo.pleaseWaitHide();
    }

    cancel() {
        this.uow.rollback();
    }
}
